/**
 * Password validation utility for frontend
 * Enforces password strength requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */

// Common weak passwords to check against
const COMMON_PASSWORDS = [
  'password',
  'password123',
  '12345678',
  '123456789',
  'qwerty',
  'abc123',
  'monkey',
  '1234567',
  'letmein',
  'trustno1',
  'dragon',
  'baseball',
  'iloveyou',
  'master',
  'sunshine',
  'ashley',
  'bailey',
  'passw0rd',
  'shadow',
  '123123',
  '654321',
  'superman',
  'qazwsx',
  'michael',
  'football'
];

/**
 * Validates password strength
 * @param {string} password - The password to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export function validatePassword(password) {
  const errors = [];

  if (!password || typeof password !== 'string') {
    return { isValid: false, errors: ['Lozinka je obavezna.'] };
  }

  // Minimum length
  if (password.length < 8) {
    errors.push('Lozinka mora imati najmanje 8 karaktera.');
  }

  // Maximum length (prevent DoS)
  if (password.length > 128) {
    errors.push('Lozinka ne može biti duža od 128 karaktera.');
  }

  // Uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Lozinka mora sadržati najmanje jedno veliko slovo (A-Z).');
  }

  // Lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Lozinka mora sadržati najmanje jedno malo slovo (a-z).');
  }

  // Number
  if (!/[0-9]/.test(password)) {
    errors.push('Lozinka mora sadržati najmanje jedan broj (0-9).');
  }

  // Special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Lozinka mora sadržati najmanje jedan specijalni karakter (!@#$%^&*...).');
  }

  // Check against common passwords (case-insensitive)
  const lowerPassword = password.toLowerCase();
  if (COMMON_PASSWORDS.some(common => lowerPassword.includes(common.toLowerCase()))) {
    errors.push('Lozinka je previše slična uobičajenoj lozinki. Molimo koristite jaču lozinku.');
  }

  // Check for repeated characters (e.g., "aaaaaa" or "111111")
  if (/(.)\1{4,}/.test(password)) {
    errors.push('Lozinka ne sme sadržati više od 4 uzastopna ista karaktera.');
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
}

/**
 * Get password requirements as an array
 * @returns {string[]} - Array of requirement strings
 */
export function getPasswordRequirements() {
  return [
    'Najmanje 8 karaktera',
    'Najmanje jedno veliko slovo (A-Z)',
    'Najmanje jedno malo slovo (a-z)',
    'Najmanje jedan broj (0-9)',
    'Najmanje jedan specijalni karakter (!@#$%^&*...)'
  ];
}

/**
 * Check individual password requirements (for real-time feedback)
 * @param {string} password - The password to check
 * @returns {Object} - Object with individual requirement checks
 */
export function checkPasswordRequirements(password) {
  if (!password) {
    return {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false
    };
  }

  return {
    length: password.length >= 8 && password.length <= 128,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };
}

